import type { PixelCanvas } from '../pixel.js';
import { mix } from '../pixel.js';
import { iso, turn } from './iso.js';
import type { FurniDrawer } from './iso.js';

/*
 * Swimming pool: water, tiled coping and plain deck tiles, laid edge to edge
 * like the running track.
 */

function diamond(pc: PixelCanvas, r: number, color: string): void {
  pc.poly([iso(-r, -r), iso(r, -r), iso(r, r), iso(-r, r)], color);
}

const WATER_DEEP = '#1f7fb8';
const WATER_MID = '#3aa3d8';
const WATER_LIGHT = '#8fd7f2';
const TILE_BASE = '#e7e2d2';
const TILE_LINE = '#c9c2ac';
const DECK_BASE = '#d8ceb4';

const drawPoolWater: FurniDrawer = (pc) => {
  diamond(pc, 0.5, WATER_DEEP);
  diamond(pc, 0.4, WATER_MID);
  diamond(pc, 0.16, WATER_LIGHT);
  // A couple of ripple lines to break up the flat fill.
  for (const v of [-0.18, 0.12]) {
    const a = iso(-0.3, v);
    const b = iso(0.3, v);
    pc.line(a.x, a.y, b.x, b.y, WATER_LIGHT);
  }
};

/** The tiled lip around the water, one straight edge. */
const drawPoolEdgeStraight: FurniDrawer = (pc, rotation) => {
  diamond(pc, 0.5, TILE_BASE);
  diamond(pc, 0.44, mix(TILE_BASE, '#ffffff', 0.15));
  // All four rotations (0/2/4/6) turn this same local shape, so each one lands
  // the water sliver on a different side instead of only two looking distinct.
  const at = (u: number, v: number) => {
    const [tu, tv] = turn(u, v, rotation);
    return iso(tu, tv);
  };
  const a = at(-0.1, -0.44);
  const b = at(-0.1, 0.44);
  pc.line(a.x, a.y, b.x, b.y, TILE_LINE);
  // A sliver of water lapping at the near edge.
  const w0 = at(0.5, -0.5);
  const w1 = at(0.5, 0.5);
  const w2 = at(0.14, 0.5);
  const w3 = at(0.14, -0.5);
  pc.poly([w0, w1, w2, w3], WATER_MID);
};

/** Turns the pool edge 90 degrees; the seam direction rotates like running_track_corner's lane lines. */
const drawPoolEdgeCorner: FurniDrawer = (pc, rotation) => {
  diamond(pc, 0.5, TILE_BASE);
  diamond(pc, 0.44, mix(TILE_BASE, '#ffffff', 0.15));
  diamond(pc, 0.22, WATER_MID);
  const [u1, v1] = turn(0.5, -0.06, rotation);
  const [u2, v2] = turn(-0.06, 0.5, rotation);
  const a = iso(u1, v1);
  const b = iso(u2, v2);
  pc.line(a.x, a.y, b.x, b.y, TILE_LINE);
};

const drawPoolDeck: FurniDrawer = (pc) => {
  diamond(pc, 0.5, DECK_BASE);
  diamond(pc, 0.46, mix(DECK_BASE, '#ffffff', 0.2));
  const h = [iso(-0.5, 0), iso(0.5, 0)] as const;
  const v = [iso(0, -0.5), iso(0, 0.5)] as const;
  pc.line(h[0].x, h[0].y, h[1].x, h[1].y, TILE_LINE);
  pc.line(v[0].x, v[0].y, v[1].x, v[1].y, TILE_LINE);
};

/** Drawers for pool furniture. */
export const POOL_DRAWERS: Record<string, FurniDrawer> = {
  pool_water: drawPoolWater,
  pool_edge_straight: drawPoolEdgeStraight,
  pool_edge_corner: drawPoolEdgeCorner,
  pool_deck: drawPoolDeck,
};
